"use client";

import { Tabs, Button, Card, Table, Grid, Popover, Tag, Upload, Modal, Empty, Space } from "antd";
import {
  UploadOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  LeftOutlined,
  RightOutlined,
  MoreOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { Row, Col } from "react-bootstrap";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { createReport, getAppointmentReports } from "../../../services/lab/lab_appointment";
import { useMessageApi } from "../../../services/message api/MessageProvider";
import api from "../../../services/axiosConfig";
import { getPreconsultData } from "../../../services/doctor/consultant/consultant";

const { Dragger } = Upload;
const { useBreakpoint } = Grid;

export default function Patients_report_detail({
  patient,
  onVitalsClick,
  appoimentBooked
}) {
  const screens = useBreakpoint();
  const [caseSheets, setCaseSheets] = useState([]);
  const [caseSheetIndex, setCaseSheetIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState("");
  const messageApi = useMessageApi();
  const [recentReports, setRecentReports] = useState([]);
  const [investigations, setInvestigations] = useState([]);

  // Responsive Font Logic
  const getFontSize = (base, sm, xs) => {
    if (screens.xs) return xs;
    if (screens.sm) return sm;
    return base;
  };

  // Mock Fetching Investigations requested by Doctor
  useEffect(() => {
    const fetchLabRequests = async () => {
      try {
        setLoading(true);
        const result = await getPreconsultData(
          patient?.id
        );
        if (result.success) {
          console.log("fetchLabAppointments Data:", result?.data?.investigations);
setInvestigations(result?.data?.investigations || []); // <-- Fixed to parentheses
        } else {
          console.error("Error:", result.error || "Failed to fetch Patient Reports");
          setInvestigations([]);
        }
      } catch (err) {
        console.error("Exception:", err.message);
        setInvestigations([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLabRequests();

    fetchLabAppointments();
  }, [patient]);

  const allowedExtensions = [".pdf", ".jpg", ".jpeg", ".png", ".doc", ".docx"];
  const allowedMimeTypes = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ];

  const currentCase = caseSheets[caseSheetIndex] || {};
  const handleUpload = async (options) => {
    const { file, onSuccess, onError } = options;

    if (!file) return;

    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf("."));

    // Check extension
    if (!allowedExtensions.includes(fileExtension)) {
      const errorMsg = "Only PDF, JPG, JPEG, PNG, DOC, DOCX files are allowed.";
      messageApi.error(errorMsg);
      onError(new Error(errorMsg));

      return; // ❌ STOP API CALL
    }

    // Optional: Check MIME type
    if (!allowedMimeTypes.includes(file.type)) {
      const errorMsg = "Invalid file type selected.";
      messageApi.error(errorMsg);
      onError(new Error(errorMsg));

      return; // ❌ STOP API CALL
    }
    // 1. Initialize FormData to match Postman 'form-data' body
    const formData = new FormData();

    // 2. Append keys exactly as seen in your Postman screenshot
    // Ensure patient?.id is a string or number as required
    formData.append("patient_id", String(patient?.patient?.id));
    if (patient?.id) {
      formData.append("appointment_id", String(patient?.id));
    }
    // formData.append("status", "Done");

    /** * IMPORTANT: 
     * In customRequest, the 'file' argument is an object.
     * Your backend error "must be a file" occurs if you send the object.
     * We must send the raw file binary.
     */
    formData.append("report_file", file);

    try {
      // 3. Call your API service
      const response = await api.post('/appointment-reports/create', formData, {
        headers: {
          // This is the magic line: 
          // Setting Content-Type to undefined or deleting it 
          // forces Axios to let the browser handle the multipart boundary.
          'Content-Type': 'multipart/form-data',
        }
      });

      // 4. Handle Response based on your API's success structure
      if (response && (response.data.message_type == "success")) {
        onSuccess(response.data);
        messageApi.success(`${file.name} uploaded successfully.`);

        // Optional: Refresh your data list here
        // fetchLabRequests(); 
      } else {
        // Handle custom backend error messages
        const errorMsg = response?.message?.report_file?.[0] || "Upload failed";
        messageApi.error(errorMsg);
        onError(new Error(errorMsg));
      }
    } catch (err) {
      messageApi.error("Network error: Could not upload file.");
      onError(err);
      console.error("Upload error:", err);
    }
  };

  const fetchLabAppointments = async () => {
    try {
      setLoading(true);
      const result = await getAppointmentReports(
        String(patient?.patient?.id),
        5
      );
      if (result.success) {
        // console.log("fetchLabAppointments Data:", result);
        setRecentReports(result.data);
      } else {
        console.error("Error:", result.error || "Failed to fetch Patient Reports");
        setRecentReports([]);
      }
    } catch (err) {
      console.error("Exception:", err.message);
      setRecentReports([]);
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    name: 'report_file',
    customRequest: handleUpload,
    multiple: false,
    // Optional: add accept attribute to the file picker
    accept: ".pdf,.jpg,.jpeg,.png,.doc,.docx",
  };


  const handlePreview = (fileUrl) => {
    setPreviewFile(fileUrl);
    setPreviewOpen(true);
  };

  return (
    <>
      <div className="h-100 d-flex flex-column" style={{ background: "#f4f7f6", overflow: "hidden" }}>

        <div className="flex-grow-1 p-3 overflow-auto">
          <Row className="g-3">
            {/* Left Column: List of Requested Tests */}
            <Col xs={12} lg={4}>
              <Card
                title="Requested Tests"
                className="shadow-sm"
                /* ✅ Uses your local state array length */
                extra={<Tag color="blue">{investigations?.length || 0} Total</Tag>}
              >
                {/* ✅ Checks your local state array length */}
                {investigations?.length > 0 ? (
                  <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {/* ✅ Map directly over your local investigations state */}
                    {investigations.map((test, index) => (
                      <div key={test.id || index} className="p-2 mb-2 border rounded d-flex justify-content-between align-items-center bg-light">
                        <div>
                          {/* Displays test name, e.g., "X-ray left knee AP and Lateral" */}
                          <div className="fw-bold">{test.name}</div>

                          {/* Type badge to make it look cleaner */}
                          {test.type && (
                            <div className="text-muted small" style={{ fontSize: '11px' }}>
                              Type: {test.type}
                            </div>
                          )}

                          {/* Optional fallback context if currentCase date isn't ready */}
                          <small className="text-muted d-block mt-1">
                            Ref: {currentCase?.appointment_date || "Today"}
                          </small>
                        </div>

                        {/* ✅ Will default to 'Pending' (Orange) since array payload doesn't contain a status */}
                        <Tag color={test.status === 'Completed' ? 'green' : 'orange'}>
                          {test.status || 'Pending'}
                        </Tag>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Empty description="No Lab Requests Found" />
                )}
              </Card>
            </Col>

            {/* Right Column: Upload & Preview Area */}
            <Col xs={12} lg={8}>
              <Card
                className="shadow-sm mb-3"
                title={
                  <div className="d-flex align-items-center gap-2">
                    <InboxOutlined /> Upload Lab Reports
                  </div>
                }
                extra={
                  <div className="d-flex gap-2">
                  </div>
                }
              >
                <div className="p-3 border-dashed rounded text-center mb-4" style={{ border: '2px dashed #d9d9d9', background: '#fafafa' }}>
                  <Dragger {...uploadProps}>
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined style={{ color: '#2e37a4' }} />
                    </p>
                    <p className="ant-upload-text">Click or drag report files to this area to upload</p>
                    <p className="ant-upload-hint">Support for PDF, JPG, PNG. Max file size: 10MB.</p>
                  </Dragger>
                </div>

                <h6 className="mt-4 mb-3">Recent Uploads & Previews</h6>
                <Table
                  pagination={false}
                  dataSource={recentReports || []}
                  size="small"
                  columns={[
                    {
                      title: 'Report File',
                      dataIndex: 'file_name',
                      key: 'report_file',
                      render: (text, record) => (
                        <>{record.report_file}</>
                      ),
                    },
                    {
                      title: 'Upload Date',
                      dataIndex: 'created_at',
                      key: 'created_at',
                      render: (text, record) => (
                        <>{dayjs(record.created_at).format("DD MMM YYYY hh:mm A")}</>
                      ),
                    },
                    {
                      title: 'Action',
                      key: 'action',
                      render: (_, record) => (
                        <Space>
                          <Button size="small" icon={<EyeOutlined />} onClick={() => handlePreview(record?.report_file_url)}>Preview</Button>
                        </Space>
                      )
                    }
                  ]}
                />
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Report Preview Modal */}
      <Modal
        title="Report Preview"
        centered
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={[<Button key="close" onClick={() => setPreviewOpen(false)}>Close</Button>]}
        width={1000}
      >
        <div style={{ height: '70vh' }}>
          {previewFile ? (
            <div>
              {previewFile.endsWith('.pdf') ? (
                <iframe src={previewFile} width="100%" height="100%" title="Report" />
              ) : (
                <img src={previewFile} alt="Report Preview" style={{ width: '100%', objectFit: 'contain' }} />
              )}
            </div>
          ) : (
            <div className="h-100 d-flex align-items-center justify-content-center">No preview available</div>
          )}
        </div>
      </Modal>
    </>
  );
}
Patients_report_detail.propTypes = {
  patient: PropTypes.object,
  onVitalsClick: PropTypes.func.isRequired,
  appoimentBooked: PropTypes.bool,
};
